import {
  IRichParagraph,
  paragraphsOfNode,
  plainTextOfParagraphs,
} from './richTextModel';

/**
 * Das Modell des nativen Entscheidungsbaum-Editors (#1245).
 *
 * Der Editor arbeitet unmittelbar auf dem kanonischen Modell — denselben Knoten,
 * die auch der Ablaufviewer und die Runtime lesen. Es gibt keine
 * Editor-Zwischenform, die auseinanderlaufen könnte.
 *
 * Reine Funktionen, weil hier die Fachlichkeit steckt: was ein neues Element
 * mitbringt, was beim Verschieben passiert, welche Fragen für eine Bedingung zur
 * Verfügung stehen. Das gehört geprüft, nicht durchgeklickt.
 */

export type { IRichParagraph };

export type NodeKind =
  | 'text'
  | 'table'
  | 'data_field'
  | 'question'
  | 'conditional_text'
  | 'conditional_block_reference'
  | 'choice'
  | 'input'
  | 'info'
  | 'block_reference'
  | 'stop'
  | 'migration_required';

export type AnswerType =
  | 'BOOLEAN'
  | 'SINGLE_CHOICE'
  | 'TEXT'
  | 'NUMBER'
  | 'DATE';

export type ConditionOperator =
  | 'equals'
  | 'notEquals'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'contains'
  // Größenvergleiche. Sie rechnen als Zahl und gelten deshalb nur für Felder, die im
  // Selektorkatalog als Zahl gemessen wurden — eine Zeichenkette, die aussieht wie eine
  // Zahl, ist keine.
  | 'greaterThan'
  | 'lessThan'
  | 'greaterOrEqual'
  | 'lessOrEqual';

export type OperandKind = 'question' | 'selector' | 'constant';

export interface IOperand {
  type: OperandKind;
  questionId?: string;
  selector?: string;
  value?: string;
}

export interface ICondition {
  left: IOperand;
  operator: ConditionOperator;
  right: string;
}

export interface IBlockReference {
  category: string;
  area: string;
  blockName: string;
  resolvedBlockId: string | null;
}

export interface IChoiceOption {
  slot: string;
  label: string;
  text?: string | null;
  reference?: IBlockReference | null;
}

export interface IQuestionBinding {
  selector: string;
  interpretation: 'DIRECT' | 'IS_EMPTY' | 'IS_NOT_EMPTY' | 'EQUALS';
  value?: string | null;
}

export interface IEditorNode {
  type: NodeKind;
  text?: string;
  /** Formatierter Urkundentext; ohne ihn gilt `text` als ein schlichter Absatz. */
  paragraphs?: IRichParagraph[];
  selector?: string;
  label?: string | null;
  fallback?: string | null;
  questionId?: string;
  prompt?: string;
  answerType?: AnswerType;
  helpText?: string | null;
  defaultValue?: string | null;
  answerOptions?: string[];
  binding?: IQuestionBinding | null;
  condition?: ICondition | null;
  /**
   * Ein zusammengesetzter Zweig: Text und weitere Knoten in ihrer Reihenfolge, darunter
   * wieder bedingte. Steht er, gilt er — `paragraphs` bleibt dann leer. So bleibt eine
   * verschachtelte Word-Bedingung eine verschachtelte Bedingung.
   */
  nodes?: IEditorNode[];
  /** Nur bei `table`: die Zeilen. Eine Zelle ist ein eigener Absatzstrom. */
  rows?: ITableRow[];
  /** Spaltenbreiten in Twips, wie sie aus dem Import kommen. */
  widths?: number[];
  /** `true` heisst „hat Rahmen" (so merkt es sich der Import); eine Liste nennt die Kanten. */
  borders?: boolean | string[];
  options?: IChoiceOption[];
  multiple?: boolean;
  reference?: IBlockReference | null;
  reason?: string;
  /** Kennung des Grundes — nur fuer die technischen Details, nie als Haupttext. */
  reasonCode?: string;
  originalType?: string;
}

export interface ITableCell {
  paragraphs?: IRichParagraph[];
  width?: number;
  /** Verbundene Spalten. Solange eine Tabelle das hat, bleibt das Spaltenwerkzeug aus. */
  colSpan?: number;
  /** Verbundene Zeilen (`restart` / `continue`). Dann bleibt das Zeilenwerkzeug aus. */
  vMerge?: string;
}

export interface ITableRow {
  cells: ITableCell[];
}

/**
 * Hat die Tabelle verbundene Zellen?
 *
 * Eine Spalte einzufügen oder zu entfernen ist dann nicht eindeutig: eine Zelle mit `colSpan`
 * steht für mehrere Spalten, und welche davon gemeint ist, sagt das Modell nicht. Statt zu raten
 * bleibt das Werkzeug aus — eine verbundene Tabelle lässt sich weiterhin in ihren Zellen
 * bearbeiten, nur nicht umbauen.
 */
export const hasMergedColumns = (node: IEditorNode): boolean =>
  (node.rows ?? []).some(row =>
    (row.cells ?? []).some(cell => (cell.colSpan ?? 1) > 1)
  );

export const hasMergedRows = (node: IEditorNode): boolean =>
  (node.rows ?? []).some(row =>
    (row.cells ?? []).some(cell => Boolean(cell.vMerge))
  );

/** Die Spaltenzahl der breitesten Zeile — verbundene Zellen zählen mit ihrer Spannweite. */
export const tableColumnCount = (node: IEditorNode): number =>
  (node.rows ?? []).reduce(
    (widest, row) =>
      Math.max(
        widest,
        (row.cells ?? []).reduce(
          (sum, cell) => sum + Math.max(1, cell.colSpan ?? 1),
          0
        )
      ),
    0
  );

const emptyCell = (): ITableCell => ({ paragraphs: [{ runs: [] }] });

/** Eine Zeile einfügen — mit so vielen leeren Zellen, wie die Tabelle Spalten hat. */
export const addTableRow = (node: IEditorNode, at?: number): IEditorNode => {
  const rows = node.rows ?? [];
  const columns = Math.max(1, tableColumnCount(node));
  const row: ITableRow = {
    cells: Array.from({ length: columns }, () => emptyCell()),
  };
  const index = at ?? rows.length;
  return { ...node, rows: insertAt(rows, index, row) };
};

/** Eine Zeile entfernen. Die letzte bleibt: eine Tabelle ohne Zeilen kommt in Word nicht an. */
export const removeTableRow = (
  node: IEditorNode,
  index: number
): IEditorNode => {
  const rows = node.rows ?? [];
  if (rows.length <= 1) {
    return node;
  }
  return { ...node, rows: removeAt(rows, index) };
};

/** Eine Spalte einfügen — in jeder Zeile an derselben Stelle, und die Breiten ziehen mit. */
export const addTableColumn = (node: IEditorNode, at?: number): IEditorNode => {
  const rows = node.rows ?? [];
  const index = at ?? tableColumnCount(node);
  const next: IEditorNode = {
    ...node,
    rows: rows.map(row => ({
      ...row,
      cells: insertAt(
        row.cells ?? [],
        Math.min(index, (row.cells ?? []).length),
        emptyCell()
      ),
    })),
  };
  if (node.widths?.length) {
    const width = node.widths[Math.min(index, node.widths.length - 1)] ?? 2000;
    next.widths = insertAt(
      node.widths,
      Math.min(index, node.widths.length),
      width
    );
  }
  return next;
};

/** Eine Spalte entfernen. Die letzte bleibt stehen. */
export const removeTableColumn = (
  node: IEditorNode,
  index: number
): IEditorNode => {
  const rows = node.rows ?? [];
  if (tableColumnCount(node) <= 1) {
    return node;
  }
  const next: IEditorNode = {
    ...node,
    rows: rows.map(row =>
      index < (row.cells ?? []).length
        ? { ...row, cells: removeAt(row.cells ?? [], index) }
        : row
    ),
  };
  if (node.widths?.length && index < node.widths.length) {
    next.widths = removeAt(node.widths, index);
  }
  return next;
};

/** Den Inhalt einer Zelle ersetzen, ohne ihre übrigen Eigenschaften anzufassen. */
export const replaceTableCell = (
  node: IEditorNode,
  rowIndex: number,
  cellIndex: number,
  cell: ITableCell
): IEditorNode => {
  const rows = node.rows ?? [];
  const row = rows[rowIndex];
  if (!row) {
    return node;
  }
  return {
    ...node,
    rows: replaceAt(rows, rowIndex, {
      ...row,
      cells: replaceAt(row.cells ?? [], cellIndex, cell),
    }),
  };
};

export interface IEditorSection {
  sectionId: string;
  title: string;
  sortOrder?: number;
  enabled: boolean;
  condition?: ICondition | null;
  blockKey?: string | null;
  nodes: IEditorNode[];
}

/** Was im Menü „Element hinzufügen" steht — Legacy-Knoten stehen bewusst nicht darin. */
export const NODE_KIND_LABEL: Record<NodeKind, string> = {
  text: 'Text',
  table: 'Tabelle',
  data_field: 'Datenfeld',
  question: 'Frage',
  conditional_text: 'Bedingung',
  conditional_block_reference: 'Bedingter Textbaustein',
  choice: 'Auswahl',
  input: 'Eingabefeld',
  info: 'Hinweis für den Bearbeiter',
  block_reference: 'Textbaustein einfügen',
  stop: 'Dokument hier beenden',
  migration_required: 'Offene Stelle aus dem Import',
};

/** Ein Satz je Eintrag: was steht danach in der Urkunde? */
export const NODE_KIND_HINT: Partial<Record<NodeKind, string>> = {
  text: 'Fester Urkundentext.',
  data_field:
    'Ein Wert aus dem Vorgang, zum Beispiel ein Name oder der Kaufpreis.',
  question: 'Eine Frage an die Bearbeiterin; die Antwort steuert Bedingungen.',
  conditional_text: 'Text, der nur unter einer Bedingung erscheint.',
  table: 'Eine Tabelle mit Zeilen und Spalten.',
  choice: 'Mehrere Antwortmöglichkeiten mit je eigenem Text.',
  input: 'Ein Feld, das beim Erstellen der Urkunde ausgefüllt wird.',
  info: 'Ein Hinweis, der nicht im Dokument erscheint.',
  block_reference: 'Fügt einen anderen Textbaustein an dieser Stelle ein.',
  conditional_block_reference:
    'Fügt einen Textbaustein nur unter einer Bedingung ein.',
  stop: 'Alles danach bleibt weg.',
};

/**
 * Eine Bedingung, die mitten im Satz steht.
 *
 * Gemessen am Altbestand: von 3 395 bedingten Textstellen hat **keine einzige** eine
 * Absatzmarke, 68,5 % geben gar nichts aus und weitere 21 % hoechstens 15 Zeichen. Der Regelfall
 * ist also der Austausch weniger Woerter — »wurde [den/dem] Erschienenen vorgelesen« —, nicht ein
 * ganzer Abschnitt. Solche Stellen bekommen im Editor eine Zeile statt eines Formulars.
 *
 * Die Grenze ist bewusst grosszuegig: ein Absatz, kein Zeilenumbruch, hoechstens 120 Zeichen.
 * Alles darueber ist ein Block und wird auch so gezeigt.
 */
export const INLINE_CONDITION_MAX = 120;

export const isInlineCondition = (node: IEditorNode): boolean => {
  if (node.type !== 'conditional_text' || node.nodes) {
    return false;
  }
  const paragraphs = paragraphsOfNode(node);
  if (paragraphs.length > 1) {
    return false;
  }
  // Nur ein Zweig, der tatsächlich im Satz weiterläuft (`continues`, so setzt ihn der Klon). Vorher
  // galt jeder kurze Zweig als »im Satz« — auch ein neu angelegter, leerer Bedingungsblock, der
  // dann zugeklappt als »gibt nichts aus« erschien (#1245, Teil 43).
  if (!paragraphs[0]?.continues) {
    return false;
  }
  const runs = paragraphs[0]?.runs ?? [];
  if (runs.some(run => run.lineBreak)) {
    return false;
  }
  const laenge = runs.reduce(
    (summe, run) => summe + (run.text ?? '').length,
    0
  );
  return laenge <= INLINE_CONDITION_MAX;
};

/** Was die Stelle ausgibt — fuer die Zeile im Editor. */
export const inlineConditionText = (node: IEditorNode): string =>
  (paragraphsOfNode(node)[0]?.runs ?? [])
    .map(run =>
      run.selector ? `‹${run.label || run.selector}›` : (run.text ?? '')
    )
    .join('')
    .trim();

/**
 * Welche Elemente eine eigene Karte bekommen.
 *
 * Struktur ja, Fliesstext nein: ein Textabsatz soll im Editor wie Text aussehen. Sonst besteht
 * eine fuenffach verschachtelte Bedingung aus zehn gleich aussehenden weissen Kaesten, und man
 * sieht die Struktur vor lauter Raendern nicht mehr.
 */
export const CARD_NODE_KINDS: NodeKind[] = [
  'question',
  'conditional_text',
  'conditional_block_reference',
  'choice',
  'table',
  'input',
  'migration_required',
];

/** Ein Element um eine Stelle verschieben; ausserhalb der Liste passiert nichts. */
export const moveAt = <T>(list: T[], index: number, delta: number): T[] => {
  const ziel = index + delta;
  if (index < 0 || index >= list.length || ziel < 0 || ziel >= list.length) {
    return list;
  }
  const kopie = [...list];
  const [element] = kopie.splice(index, 1);
  kopie.splice(ziel, 0, element);
  return kopie;
};

/** Was man täglich braucht — diese vier stehen oben und ohne Aufklappen. */
export const COMMON_NODE_KINDS: NodeKind[] = [
  'text',
  'data_field',
  'question',
  'conditional_text',
  'table',
];

/** Der Rest: richtig, aber selten. */
export const MORE_NODE_KINDS: NodeKind[] = [
  'choice',
  'input',
  'block_reference',
  'conditional_block_reference',
  'info',
  'stop',
];

/** In dieser Reihenfolge angeboten: erst was man ständig braucht. */
export const ADDABLE_NODE_KINDS: NodeKind[] = [
  ...COMMON_NODE_KINDS,
  ...MORE_NODE_KINDS,
];

export const ANSWER_TYPE_LABEL: Record<AnswerType, string> = {
  BOOLEAN: 'Ja / Nein',
  SINGLE_CHOICE: 'Auswahl',
  TEXT: 'Text',
  NUMBER: 'Zahl',
  DATE: 'Datum',
};

export const OPERATOR_LABEL: Record<ConditionOperator, string> = {
  equals: 'ist',
  notEquals: 'ist nicht',
  isEmpty: 'ist leer',
  isNotEmpty: 'ist nicht leer',
  contains: 'enthält',
  greaterThan: 'ist größer als',
  lessThan: 'ist kleiner als',
  greaterOrEqual: 'ist mindestens',
  lessOrEqual: 'ist höchstens',
};

/** Operatoren, die eine Zahl vergleichen — nur für Felder vom Typ Zahl sinnvoll. */
export const NUMERIC_OPERATORS: ConditionOperator[] = [
  'greaterThan',
  'lessThan',
  'greaterOrEqual',
  'lessOrEqual',
];

export const isNumericOperator = (operator: ConditionOperator): boolean =>
  NUMERIC_OPERATORS.includes(operator);

/** Operatoren ohne rechte Seite — dort wäre ein Eingabefeld sinnlos. */
export const UNARY_OPERATORS: ConditionOperator[] = ['isEmpty', 'isNotEmpty'];

export const isUnary = (operator: ConditionOperator): boolean =>
  UNARY_OPERATORS.includes(operator);

/**
 * Die Kennung einer neu angelegten Frage.
 *
 * Bewusst **nicht** `frage1`, `frage2`, … — das ist ArnoTops Registernummer, und die ist keine
 * Frageidentität (Bericht Teil 28): dieselbe Nummer steht im Altbestand für bis zu fünf
 * verschiedene Fragen. Der Editor hat sie bisher trotzdem vergeben, und zwar je Abschnitt
 * gezählt: zwei Abschnitte mit je einer Frage ergaben zweimal `frage1`. Die Prüfung lehnt das
 * ab, aber erst beim Veröffentlichen.
 *
 * `used` sind alle im Baum schon vergebenen Kennungen — nicht nur die des Abschnitts.
 */
export const newQuestionId = (used: string[]): string => {
  let counter = used.length + 1;
  while (used.includes(`q${counter}`)) {
    counter += 1;
  }
  return `q${counter}`;
};

/** Ein neues Element mit brauchbaren Vorgaben statt einer leeren Hülle. */
export const newNode = (
  kind: NodeKind,
  usedQuestionIds: string[] = []
): IEditorNode => {
  switch (kind) {
    case 'text':
      return { type: 'text', text: '' };
    case 'table':
      // Zwei mal zwei ist die kleinste Tabelle, die man als Tabelle erkennt.
      return {
        type: 'table',
        rows: [
          { cells: [emptyCell(), emptyCell()] },
          { cells: [emptyCell(), emptyCell()] },
        ],
        borders: true,
      };
    case 'data_field':
      return { type: 'data_field', selector: '', label: null, fallback: null };
    case 'question':
      return {
        type: 'question',
        questionId: newQuestionId(usedQuestionIds),
        prompt: '',
        answerType: 'BOOLEAN',
        answerOptions: [],
        binding: null,
      };
    case 'conditional_text':
      return { type: 'conditional_text', condition: newCondition(), text: '' };
    case 'conditional_block_reference':
      return {
        type: 'conditional_block_reference',
        condition: newCondition(),
        reference: null,
      };
    case 'choice':
      return {
        type: 'choice',
        prompt: '',
        multiple: false,
        options: [
          { slot: '1', label: '', text: '' },
          { slot: '2', label: '', text: '' },
        ],
      };
    case 'input':
      return { type: 'input', prompt: '' };
    case 'info':
      return { type: 'info', text: '' };
    case 'block_reference':
      return { type: 'block_reference', reference: null };
    default:
      return { type: kind };
  }
};

export const newCondition = (): ICondition => ({
  left: { type: 'question', questionId: '' },
  operator: 'equals',
  right: 'true',
});

export const newSection = (existing: IEditorSection[]): IEditorSection => {
  const numbers = existing
    .map(section => Number((section.sectionId.match(/(\d+)$/) || [])[1]))
    .filter(value => !Number.isNaN(value));
  const next = (numbers.length ? Math.max(...numbers) : existing.length) + 1;
  return {
    sectionId: `abschnitt-${next}`,
    title: `Abschnitt ${existing.length + 1}`,
    enabled: true,
    condition: null,
    nodes: [],
  };
};

/** Verschieben um eine Position; am Rand passiert nichts. */
export const moveItem = <T>(items: T[], index: number, delta: number): T[] => {
  const target = index + delta;
  if (
    index < 0 ||
    index >= items.length ||
    target < 0 ||
    target >= items.length
  ) {
    return items;
  }
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
};

export const removeAt = <T>(items: T[], index: number): T[] =>
  items.filter((_, position) => position !== index);

export const replaceAt = <T>(items: T[], index: number, item: T): T[] =>
  items.map((current, position) => (position === index ? item : current));

export const insertAt = <T>(items: T[], index: number, item: T): T[] => {
  const next = [...items];
  next.splice(index, 0, item);
  return next;
};

/**
 * Ein Element duplizieren.
 *
 * Eine Frage bekommt dabei einen neuen Schlüssel: zwei Fragen mit demselben
 * Schlüssel wären derselbe Wert an zwei Stellen, und die Prüfung lehnt das ab.
 */
export const duplicateNode = (
  node: IEditorNode,
  usedQuestionIds: string[]
): IEditorNode => {
  const copy: IEditorNode = JSON.parse(JSON.stringify(node));
  if (copy.type === 'question' && copy.questionId) {
    copy.questionId = uniqueQuestionId(copy.questionId, usedQuestionIds);
  }
  return copy;
};

export const uniqueQuestionId = (base: string, used: string[]): string => {
  const clean = base.replace(/-\d+$/, '');
  if (!used.includes(clean)) {
    return clean;
  }
  let counter = 2;
  while (used.includes(`${clean}-${counter}`)) {
    counter += 1;
  }
  return `${clean}-${counter}`;
};

/** Jeder Knoten eines Abschnitts, auch die in einem zusammengesetzten Zweig. */
export const walkNodes = (nodes: IEditorNode[]): IEditorNode[] => {
  const result: IEditorNode[] = [];
  const visit = (items: IEditorNode[]) => {
    items.forEach(node => {
      result.push(node);
      if (node.nodes?.length) {
        visit(node.nodes);
      }
    });
  };
  visit(nodes);
  return result;
};

/**
 * Ein Pfad in den Baum: die Indizes von aussen nach innen.
 *
 * `[2]` ist das dritte Element eines Abschnitts, `[2, 0]` das erste Element in dessen Zweig.
 * Damit lässt sich ein verschachtelter Knoten ändern, ohne den Baum flach zu machen — und genau
 * die Verschachtelung ist der Punkt.
 */
export type NodePath = number[];

/** Den Knoten an diesem Pfad ersetzen. Ein leerer Pfad ersetzt nichts. */
export const replaceAtPath = (
  nodes: IEditorNode[],
  path: NodePath,
  next: IEditorNode
): IEditorNode[] => {
  if (path.length === 0) {
    return nodes;
  }
  const [index, ...rest] = path;
  if (index < 0 || index >= nodes.length) {
    return nodes;
  }
  if (rest.length === 0) {
    return replaceAt(nodes, index, next);
  }
  const parent = nodes[index];
  return replaceAt(nodes, index, {
    ...parent,
    nodes: replaceAtPath(parent.nodes ?? [], rest, next),
  });
};

/** Den Knoten an diesem Pfad entfernen. */
export const removeAtPath = (
  nodes: IEditorNode[],
  path: NodePath
): IEditorNode[] => {
  if (path.length === 0) {
    return nodes;
  }
  const [index, ...rest] = path;
  if (index < 0 || index >= nodes.length) {
    return nodes;
  }
  if (rest.length === 0) {
    return removeAt(nodes, index);
  }
  const parent = nodes[index];
  return replaceAt(nodes, index, {
    ...parent,
    nodes: removeAtPath(parent.nodes ?? [], rest),
  });
};

/** Einen Knoten ans Ende des Zweigs an diesem Pfad hängen. */
export const appendAtPath = (
  nodes: IEditorNode[],
  path: NodePath,
  node: IEditorNode
): IEditorNode[] => {
  if (path.length === 0) {
    return [...nodes, node];
  }
  const [index, ...rest] = path;
  if (index < 0 || index >= nodes.length) {
    return nodes;
  }
  const parent = nodes[index];
  return replaceAt(nodes, index, {
    ...parent,
    nodes: appendAtPath(parent.nodes ?? [], rest, node),
  });
};

/**
 * Was in einem Zweig stehen darf.
 *
 * Eine Frage gehört nicht dazu: sie würde nur gestellt, wenn der Zweig überhaupt genommen wird
 * — und dann hinge ihre Antwort von einer Bedingung ab, die sie selbst beeinflusst. Ein
 * Abschnitt ist der richtige Ort dafür.
 */
export const BRANCH_NODE_KINDS: NodeKind[] = [
  'text',
  'data_field',
  'conditional_text',
  'block_reference',
  'conditional_block_reference',
  'info',
];

/** Wie tief liegt ein Zweig — und was steht darin? Für die eingerückte Ansicht. */
export interface IOutlineRow {
  depth: number;
  kind: NodeKind;
  label: string;
  text: string;
  open: boolean;
}

export const conditionOutline = (
  node: IEditorNode,
  depth = 0
): IOutlineRow[] => {
  const rows: IOutlineRow[] = [
    {
      depth,
      kind: node.type,
      label:
        node.type === 'conditional_text' ? 'Wenn' : NODE_KIND_LABEL[node.type],
      text: nodeSummary(node),
      open: node.type === 'migration_required',
    },
  ];
  (node.nodes ?? []).forEach(child => {
    rows.push(...conditionOutline(child, depth + 1));
  });
  return rows;
};

/** Alle Fragen des Baums — die Auswahl einer Bedingung. */
export const questionsOf = (
  sections: IEditorSection[]
): { questionId: string; prompt: string; answerType: AnswerType }[] => {
  const result: {
    questionId: string;
    prompt: string;
    answerType: AnswerType;
  }[] = [];
  sections.forEach(section =>
    walkNodes(section.nodes).forEach(node => {
      if (node.type === 'question' && node.questionId) {
        result.push({
          questionId: node.questionId,
          prompt: node.prompt || node.questionId,
          answerType: node.answerType || 'TEXT',
        });
      }
    })
  );
  return result;
};

/**
 * Die Fragen, auf die sich eine Bedingung beziehen kann.
 *
 * Das sind nicht nur die des offenen Baums: eine Frage aus einem eingefügten Unterbaustein gilt
 * für denselben Lauf und darf ebenso abgefragt werden. Der Baum hat Vorrang — was gerade
 * bearbeitet wird, ist aktueller als das, was der Server mitgeliefert hat.
 *
 * Fragen ohne eigenen Text sind im Altbestand der Regelfall (ein Register hat keine Frage, nur
 * eine Nummer). Statt des technischen Schlüssels erscheint dann der Baustein, in dem sie
 * gestellt wird.
 */
export const questionChoices = (
  sections: IEditorSection[],
  fromVersion: {
    questionId: string;
    prompt: string;
    answerType: string;
    blockName?: string;
  }[] = []
): { questionId: string; prompt: string; answerType: AnswerType }[] => {
  const eigene = questionsOf(sections);
  const bekannt = new Set(eigene.map(item => item.questionId));
  const weitere = fromVersion
    .filter(item => item.questionId && !bekannt.has(item.questionId))
    .map(item => ({
      questionId: item.questionId,
      prompt:
        item.prompt.trim() ||
        (item.blockName ? `Frage aus „${item.blockName}"` : item.questionId),
      answerType: (item.answerType || 'TEXT') as AnswerType,
    }));
  return [...eigene, ...weitere];
};

/** Welche Bausteine sich einfügen lassen: die Abschnitte dieses Baums. */
export const blockTargetsOf = (
  sections: IEditorSection[],
  current?: string,
  subBlocks: { blockKey: string; name: string }[] = []
): { blockKey: string; title: string }[] => {
  const abschnitte = sections
    .filter(section => section.blockKey && section.sectionId !== current)
    .map(section => ({
      blockKey: section.blockKey as string,
      title: section.title,
    }));
  // Unterbausteine eines Klons sind ebenso gültige Ziele. Ohne sie stand ein Verweis auf einen
  // von ihnen im Editor als leeres Auswahlfeld da (Sichtprüfung Teil 40).
  const bekannt = new Set(abschnitte.map(entry => entry.blockKey));
  const unter = subBlocks
    .filter(entry => entry.blockKey && !bekannt.has(entry.blockKey))
    .map(entry => ({
      blockKey: entry.blockKey,
      title: `Unterbaustein · ${entry.name}`,
    }));
  return [...abschnitte, ...unter];
};

/** Die Zeile unter einem Abschnitt in der Liste. */
export const sectionSummary = (section: IEditorSection): string => {
  const count = section.nodes.length;
  const parts = [count === 1 ? '1 Element' : `${count} Elemente`];
  if (section.condition) {
    parts.push('bedingt');
  }
  if (!section.enabled) {
    parts.push('nicht aktiv');
  }
  return parts.join(' · ');
};

/** Womit ein Element in der Liste überschrieben ist. */
export const nodeSummary = (node: IEditorNode): string => {
  switch (node.type) {
    case 'text':
      // Der formatierte Text steht in den Absaetzen; `text` traegt nur der Altbestand.
      return plainTextOfParagraphs(paragraphsOfNode(node)).trim() || '(leer)';
    case 'info':
      return (node.text || '').trim() || '(leer)';
    case 'data_field':
      return node.label || node.selector || '(kein Feld gewählt)';
    case 'question':
      return (node.prompt || '').trim() || '(keine Frage formuliert)';
    case 'conditional_text': {
      const own = plainTextOfParagraphs(paragraphsOfNode(node)).trim();
      if (own) {
        return own;
      }
      // Ein zusammengesetzter Zweig hat keinen eigenen Text; er besteht aus seinen Knoten.
      const children = node.nodes ?? [];
      if (!children.length) {
        return '(leer)';
      }
      const inner = children.filter(
        child => child.type === 'conditional_text'
      ).length;
      const preview = children
        .map(child => nodeSummary(child))
        .join(' ')
        .slice(0, 80);
      return inner
        ? `${preview} · ${inner} weitere Bedingung${inner === 1 ? '' : 'en'} darin`
        : preview;
    }
    case 'choice':
      return (node.prompt || '').trim() || '(keine Frage formuliert)';
    case 'input':
      return (node.prompt || '').trim() || '(keine Beschriftung)';
    case 'block_reference':
    case 'conditional_block_reference':
      return node.reference?.blockName || '(kein Baustein gewählt)';
    case 'stop':
      return 'Der Ablauf endet hier';
    case 'migration_required':
      return node.reason || 'Offene Stelle';
    default:
      return NODE_KIND_LABEL[node.type] ?? node.type;
  }
};

/** Bedingung im Klartext — dieselbe Sprache wie im Ablaufviewer. */
export const conditionSummary = (
  condition: ICondition | null | undefined,
  questions: { questionId: string; prompt: string }[],
  /** Klartextname zu einem Feldpfad, wenn die Oberflaeche ihn kennt (#1245). */
  fieldLabel?: (_path: string) => string | undefined
): string => {
  if (!condition) {
    return '';
  }
  const { left, operator, right } = condition;
  let subject = '';
  if (left.type === 'question') {
    const question = questions.find(
      item => item.questionId === left.questionId
    );
    subject = question
      ? `„${question.prompt}"`
      : `Frage ${left.questionId || '?'}`;
  } else if (left.type === 'selector') {
    subject =
      (left.selector ? fieldLabel?.(left.selector) : undefined) ||
      left.selector ||
      'Datenfeld';
  } else {
    subject = `„${left.value ?? ''}"`;
  }
  const verb = OPERATOR_LABEL[operator];
  return isUnary(operator)
    ? `Wenn ${subject} ${verb}`
    : `Wenn ${subject} ${verb} „${answerLabel(right)}"`;
};

const COUNTER_OPERATOR: Partial<Record<ConditionOperator, ConditionOperator>> =
  {
    equals: 'notEquals',
    notEquals: 'equals',
    isEmpty: 'isNotEmpty',
    isNotEmpty: 'isEmpty',
    greaterThan: 'lessOrEqual',
    lessOrEqual: 'greaterThan',
    lessThan: 'greaterOrEqual',
    greaterOrEqual: 'lessThan',
  };

/**
 * Die Gegenbedingung — so entsteht ein »sonst« (#1245, Teil 43). Das Modell kennt keinen eigenen
 * Sonst-Zweig; der Import legt eine zweite Bedingung mit dem Gegenteil an, und genauso hier.
 * `null`, wo es kein sicheres Gegenteil gibt (»enthält«).
 */
export const negateCondition = (
  condition: ICondition | null | undefined
): ICondition | null => {
  const operator = condition ? COUNTER_OPERATOR[condition.operator] : undefined;
  return condition && operator ? { ...condition, operator } : null;
};

/** Ja/Nein statt true/false — im Editor steht die Fachsprache, nicht der Wert. */
export const answerLabel = (value: string): string => {
  if (value === 'true') {
    return 'Ja';
  }
  if (value === 'false') {
    return 'Nein';
  }
  return value;
};

/** Ob sich gegenüber dem geladenen Stand etwas geändert hat. */
export const isDirty = (
  loaded: IEditorSection[],
  current: IEditorSection[]
): boolean => JSON.stringify(loaded) !== JSON.stringify(current);
